import { useState, useCallback } from 'react';
import {
  useSensor,
  useSensors,
  PointerSensor,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { notifications } from '@mantine/notifications';
import type { GroupWithMembers } from '../../../../services/api/groups';
import { groupsApi } from '../../../../services/api/groups';
import type { CohortCoachWithDetails } from '../../../../services/api/cohortCoaches';
import {
  collectAssignmentWarnings,
  type CoachAssignmentWarning,
  type GroupSessionsMap,
} from './coachConflictUtils';

interface UseCoachCardDragDropOptions {
  cohortId: string;
  groups: GroupWithMembers[];
  // MED-978: sessions-by-group used to filter date-level conflicts to each
  // group's actual scheduled dates.
  groupSessionsMap: GroupSessionsMap;
  onRefresh: () => void | Promise<void>;
  fetchCalloutData: () => Promise<void>;
}

export interface PendingDrop {
  coach: CohortCoachWithDetails;
  group: GroupWithMembers;
  warnings: CoachAssignmentWarning[];
  isReplacing: boolean;
}

export interface UseCoachCardDragDropResult {
  /** The coach currently being dragged (for DragOverlay) */
  activeDragCoach: CohortCoachWithDetails | null;
  /** Sensor configuration for dnd-kit */
  sensors: ReturnType<typeof useSensors>;
  /** Handler for drag start events */
  handleDragStart: (event: DragStartEvent) => void;
  /** Handler for drag end events — checks for conflicts before assignment */
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
  /** Pending drop awaiting user confirmation (null if none) */
  pendingDrop: PendingDrop | null;
  /** Confirm the pending drop — execute the assignment */
  confirmPendingDrop: () => Promise<void>;
  /** Cancel the pending drop */
  cancelPendingDrop: () => void;
}

/**
 * Manages drag-and-drop state and handlers for dragging coaches
 * from the side panel onto group assignment cards.
 *
 * MED-434: Now checks for conflicts/warnings before executing.
 * If warnings exist or replacing an existing coach, defers to a
 * confirmation modal via `pendingDrop` state.
 */
export function useCoachCardDragDrop({
  cohortId,
  groups,
  groupSessionsMap,
  onRefresh,
  fetchCalloutData,
}: UseCoachCardDragDropOptions): UseCoachCardDragDropResult {
  const [activeDragCoach, setActiveDragCoach] =
    useState<CohortCoachWithDetails | null>(null);
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const coach = active.data.current?.coach as CohortCoachWithDetails | undefined;
    if (coach) {
      setActiveDragCoach(coach);
    }
  }, []);

  /** Execute the actual assignment API call */
  const executeAssignment = useCallback(
    async (coach: CohortCoachWithDetails, targetGroup: GroupWithMembers) => {
      try {
        await groupsApi.update(cohortId, targetGroup.id, { coachId: coach.user_id });

        const coachName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim();
        notifications.show({
          title: 'Coach Assigned',
          message: `${coachName} assigned to ${targetGroup.name}`,
          color: 'green',
        });

        await onRefresh();
        await fetchCalloutData();
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to assign coach',
          color: 'red',
        });
      }
    },
    [cohortId, onRefresh, fetchCalloutData],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragCoach(null);

      if (!over) return;

      const coach = active.data.current?.coach as CohortCoachWithDetails | undefined;
      if (!coach) return;

      const dropId = String(over.id);

      // Drop on a group card → assign coach to that group
      if (dropId.startsWith('coach-drop-group-')) {
        const groupId = dropId.replace('coach-drop-group-', '');
        const targetGroup = groups.find((g) => g.id === groupId);

        if (!targetGroup) return;

        // Already assigned to this coach
        if (targetGroup.coach_id === coach.user_id) {
          notifications.show({
            title: 'Already Assigned',
            message: `${coach.first_name} ${coach.last_name} is already assigned to ${targetGroup.name}`,
            color: 'blue',
          });
          return;
        }

        // Check for warnings (MED-978: filtered to this group's session dates)
        const warnings = collectAssignmentWarnings(coach, [targetGroup], groupSessionsMap);
        const isReplacing = Boolean(targetGroup.coach_id);

        // If there are warnings or replacing an existing coach, defer to modal
        if (warnings.length > 0 || isReplacing) {
          setPendingDrop({ coach, group: targetGroup, warnings, isReplacing });
          return;
        }

        // Clean assignment — execute directly
        await executeAssignment(coach, targetGroup);
      }
    },
    [groups, groupSessionsMap, executeAssignment],
  );

  const confirmPendingDrop = useCallback(async () => {
    if (!pendingDrop) return;
    const { coach, group } = pendingDrop;
    setPendingDrop(null);
    await executeAssignment(coach, group);
  }, [pendingDrop, executeAssignment]);

  const cancelPendingDrop = useCallback(() => {
    setPendingDrop(null);
  }, []);

  return {
    activeDragCoach,
    sensors,
    handleDragStart,
    handleDragEnd,
    pendingDrop,
    confirmPendingDrop,
    cancelPendingDrop,
  };
}
